import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import { toast } from 'sonner';
import { FlaskConical, Upload, ArrowUp, ArrowDown, ExternalLink, Trash2, Eye, Printer, TrendingUp } from 'lucide-react';
import { openPrintWindow } from '@/lib/letterhead';
import ConfirmDialog from '@/components/ConfirmDialog';
import DatePickerField from '@/components/DatePickerField';

export const LabTab = ({ ospiteId, ospite }) => {
  const { hasRole } = useAuth();
  const [referti, setReferti] = useState([]);
  const [trend, setTrend] = useState(null);
  const [sel, setSel] = useState(null);
  const [analitaSel, setAnalitaSel] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [dataReferto, setDataReferto] = useState('');
  const [uploading, setUploading] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const token = localStorage.getItem('rsa_token');

  const load = useCallback(async () => {
    try {
      const [r, t] = await Promise.all([
        api.get('/referti-lab', { params: { ospite_id: ospiteId } }),
        api.get(`/referti-lab/trend/${ospiteId}`),
      ]);
      setReferti(r.data);
      setTrend(t.data);
      setSel(prev => (prev ? r.data.find(x => x.id === prev.id) || r.data[0] || null : r.data[0] || null));
      setAnalitaSel(prev => prev || t.data.analiti?.[0]?.analita || null);
    } catch { setReferti([]); }
  }, [ospiteId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!file) { toast.error('Seleziona il file del referto'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('ospite_id', ospiteId);
    if (dataReferto) fd.append('data_referto', dataReferto);
    try {
      const res = await api.post('/referti-lab/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Referto letto: ${res.data.analiti?.length || 0} analiti, ${res.data.valori_alterati} fuori range`);
      setShowUpload(false); setFile(null); setDataReferto('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore caricamento'); }
    setUploading(false);
  };

  const segnaVisto = async (id) => {
    try { await api.put(`/referti-lab/${id}/visto`); toast.success('Presa visione registrata'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const elimina = async () => {
    try { await api.delete(`/referti-lab/${confirmDel}`); toast.success('Referto eliminato'); setSel(null); setConfirmDel(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const stampa = () => {
    if (!sel) return;
    const nome = `${ospite?.cognome || ''} ${ospite?.nome || ''}`;
    let body = `<div class="info-grid">
      <div class="info-item"><span class="info-label">Ospite</span> <span class="info-value">${nome}</span></div>
      <div class="info-item"><span class="info-label">Data referto</span> <span class="info-value">${sel.data_referto}</span></div>
      <div class="info-item"><span class="info-label">Laboratorio</span> <span class="info-value">${sel.laboratorio || '-'}</span></div>
      <div class="info-item"><span class="info-label">Valori alterati</span> <span class="info-value">${sel.valori_alterati}</span></div>
    </div>`;
    body += `<p class="section-title">Analiti</p><table><thead><tr><th>Esame</th><th>Valore</th><th>Unita</th><th>Riferimento</th><th>Esito</th></tr></thead><tbody>`;
    (sel.analiti || []).forEach(a => {
      body += `<tr><td>${a.nome}</td><td${a.fuori_range ? ' style="color:#dc2626;font-weight:700"' : ''}>${a.valore ?? '-'}</td><td>${a.unita || ''}</td><td>${a.range_testo || ''}</td><td>${a.fuori_range ? `<span class="badge badge-red">${a.direzione || 'fuori range'}</span>` : '<span class="badge badge-green">nei limiti</span>'}</td></tr>`;
    });
    body += `</tbody></table>`;
    if (sel.sintesi_ai) body += `<p class="section-title">Sintesi (lettura AI)</p><p>${sel.sintesi_ai}</p>`;
    body += `<p style="font-size:9px;color:#64748b;margin-top:6px">Valori estratti automaticamente dal referto tramite AI: verificare sempre sul documento originale. Confidenza: ${sel.confidenza_ai || '-'}</p>`;
    body += `<div class="signature-row"><div class="signature-block">Il Medico</div><div class="signature-block">Data</div></div>`;
    openPrintWindow('Referto di Laboratorio', body, { subtitle: `${nome} - ${sel.data_referto}` });
  };

  const serieAnalita = trend?.analiti?.find(a => a.analita === analitaSel);
  const chartData = (serieAnalita?.valori || []).filter(v => v.valore !== null).map(v => ({
    data: new Date(v.data).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }),
    valore: v.valore,
  }));

  return (
    <div className="space-y-4" data-testid="lab-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><FlaskConical className="w-4 h-4 text-brand" />Analisi di Laboratorio</h3>
        <div className="flex gap-2">
          {sel && <Button size="sm" variant="outline" onClick={stampa} data-testid="lab-print-btn"><Printer className="w-3.5 h-3.5 mr-1" />Stampa</Button>}
          {hasRole('infermiere', 'coordinatore', 'medico', 'amministrazione') && (
            <Dialog open={showUpload} onOpenChange={setShowUpload}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-brand hover:bg-brand-dark text-white" data-testid="lab-upload-btn"><Upload className="w-3.5 h-3.5 mr-1" />Carica referto</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuovo Referto di Laboratorio</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">File del referto (JPG, PNG, PDF - max 15 MB) *</Label>
                    <Input type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
                      onChange={e => setFile(e.target.files?.[0] || null)} data-testid="lab-file-input" />
                    <p className="text-[10px] text-slate-400 mt-1">L'IA estrae automaticamente tutti gli analiti e i valori di riferimento</p>
                  </div>
                  <div><Label className="text-xs">Data referto (se non leggibile dal documento)</Label>
                    <DatePickerField value={dataReferto} onChange={v => setDataReferto(v)} testId="lab-data-input" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowUpload(false)}>Annulla</Button>
                    <Button onClick={handleUpload} disabled={uploading} className="bg-brand hover:bg-brand-dark text-white" data-testid="lab-save-upload-btn">
                      {uploading ? 'Lettura in corso...' : 'Carica e analizza'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {referti.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-slate-200 rounded-xl">
          <FlaskConical className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-xs text-slate-400">Nessun referto di laboratorio caricato</p>
        </div>
      ) : (
        <Tabs defaultValue="referti">
          <TabsList>
            <TabsTrigger value="referti" className="text-xs" data-testid="lab-tab-referti">Referti ({referti.length})</TabsTrigger>
            <TabsTrigger value="trend" className="text-xs" data-testid="lab-tab-trend"><TrendingUp className="w-3.5 h-3.5 mr-1" />Confronto nel tempo</TabsTrigger>
          </TabsList>

          <TabsContent value="referti" className="mt-3">
            <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
              <div className="space-y-2" data-testid="lab-lista">
                {referti.map(r => (
                  <button key={r.id} onClick={() => setSel(r)}
                    className={`w-full text-left p-2.5 rounded-lg border text-xs transition-colors ${sel?.id === r.id ? 'border-brand bg-brand-soft' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`lab-item-${r.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-slate-800">{new Date(r.data_referto).toLocaleDateString('it-IT')}</span>
                      {r.valori_alterati > 0 && <Badge className="text-[9px] bg-red-100 text-red-700">{r.valori_alterati} alterati</Badge>}
                    </div>
                    <p className="text-slate-500 mt-0.5 truncate">{r.laboratorio || 'Laboratorio n/d'}</p>
                    {!r.visto_da_medico && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 inline-block mt-1">da visionare</span>}
                  </button>
                ))}
              </div>

              {sel && (
                <div className="space-y-3" data-testid="lab-dettaglio">
                  <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl border border-slate-200 bg-white">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Referto del {new Date(sel.data_referto).toLocaleDateString('it-IT')}</p>
                      <p className="text-xs text-slate-500">{sel.laboratorio || 'Laboratorio n/d'} · {(sel.analiti || []).length} analiti · caricato da {sel.caricato_da}</p>
                      {sel.match_motivo && <p className="text-[10px] text-slate-400">Riconoscimento paziente: {sel.match_motivo}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" asChild data-testid="lab-open-file-btn">
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/referti-lab/${sel.id}/file?auth=${token}`} target="_blank" rel="noreferrer">
                          <ExternalLink className="w-3.5 h-3.5 mr-1" />Apri originale
                        </a>
                      </Button>
                      {hasRole('medico', 'coordinatore') && !sel.visto_da_medico && (
                        <Button size="sm" onClick={() => segnaVisto(sel.id)} className="bg-brand hover:bg-brand-dark text-white" data-testid="lab-visto-btn">
                          <Eye className="w-3.5 h-3.5 mr-1" />Presa visione
                        </Button>
                      )}
                      {hasRole('coordinatore', 'medico') && (
                        <Button size="sm" variant="outline" onClick={() => setConfirmDel(sel.id)} className="text-red-600" data-testid="lab-delete-btn">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {sel.visto_da_medico && (
                    <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-2 py-1">
                      Presa visione da {sel.visto_da_medico.da} il {new Date(sel.visto_da_medico.il).toLocaleString('it-IT')}
                    </p>
                  )}

                  {sel.sintesi_ai && (
                    <div className="p-3 rounded-xl border border-violet-200 bg-violet-50/40 text-xs">
                      <p className="font-semibold text-slate-800 mb-1">Sintesi della lettura AI</p>
                      <p className="text-slate-700">{sel.sintesi_ai}</p>
                      <p className="text-[10px] text-slate-500 mt-1 italic">Valori estratti automaticamente: verificare sul documento originale. Confidenza {sel.confidenza_ai || 'n/d'}. {sel.limiti_ai}</p>
                    </div>
                  )}

                  <div className="rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full text-[11px]">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr><th className="text-left px-2 py-1.5">Esame</th><th className="px-2">Valore</th><th className="px-2">Unita</th><th className="px-2">Riferimento</th><th className="px-2">Esito</th></tr>
                      </thead>
                      <tbody>
                        {(sel.analiti || []).map((a, i) => (
                          <tr key={i} className={`border-t border-slate-100 ${a.fuori_range ? 'bg-red-50/50' : ''}`} data-testid={`lab-analita-${i}`}>
                            <td className="px-2 py-1.5 font-medium text-slate-700">{a.nome}</td>
                            <td className={`px-2 text-center font-semibold ${a.fuori_range ? 'text-red-600' : 'text-slate-700'}`}>{a.valore ?? '-'}</td>
                            <td className="px-2 text-center text-slate-500">{a.unita || ''}</td>
                            <td className="px-2 text-center text-slate-500">{a.range_testo || ''}</td>
                            <td className="px-2 text-center">
                              {a.fuori_range ? (
                                <span className="inline-flex items-center gap-0.5 text-red-600 font-semibold">
                                  {a.direzione === 'basso' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}{a.direzione || 'fuori range'}
                                </span>
                              ) : <span className="text-emerald-600">nei limiti</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="trend" className="mt-3">
            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
              <div className="space-y-1.5 max-h-[460px] overflow-y-auto" data-testid="lab-analiti-lista">
                {(trend?.analiti || []).map((a, i) => (
                  <button key={i} onClick={() => setAnalitaSel(a.analita)}
                    className={`w-full text-left p-2 rounded-lg border text-xs transition-colors ${analitaSel === a.analita ? 'border-brand bg-brand-soft' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`lab-analita-trend-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-slate-800 truncate">{a.analita}</span>
                      {a.alterato_ultimo && <Badge className="text-[9px] bg-red-100 text-red-700 flex-shrink-0">alterato</Badge>}
                    </div>
                    <p className="text-slate-500 mt-0.5">
                      ultimo: <span className={a.alterato_ultimo ? 'text-red-600 font-semibold' : 'font-semibold'}>{a.ultimo ?? '-'}</span> {a.unita}
                      {a.variazione_pct !== null && a.variazione_pct !== undefined && (
                        <span className={`ml-1 ${a.variazione_pct > 0 ? 'text-amber-600' : 'text-sky-600'}`}>
                          ({a.variazione_pct > 0 ? '+' : ''}{a.variazione_pct}%)
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] text-slate-400">{a.n_rilevazioni} rilevazioni · rif. {a.range_testo || 'n/d'}</p>
                  </button>
                ))}
              </div>

              <div className="space-y-3">
                {!serieAnalita ? (
                  <p className="text-xs text-slate-400 py-8 text-center">Seleziona un analita per vedere l'andamento</p>
                ) : (
                  <>
                    <div className="p-3 rounded-xl border border-slate-200 bg-white" data-testid="lab-trend-chart">
                      <p className="text-sm font-semibold text-slate-800 mb-2">{serieAnalita.analita} {serieAnalita.unita && `(${serieAnalita.unita})`}</p>
                      {chartData.length < 2 ? (
                        <p className="text-xs text-slate-400 py-8 text-center">Serve almeno un secondo referto per il confronto nel tempo</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={220}>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="data" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
                            <Tooltip contentStyle={{ fontSize: 11 }} />
                            {serieAnalita.range_min !== null && serieAnalita.range_max !== null && (
                              <ReferenceArea y1={serieAnalita.range_min} y2={serieAnalita.range_max} fill="#076633" fillOpacity={0.08} />
                            )}
                            <Line type="monotone" dataKey="valore" stroke="#076633" strokeWidth={2} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                    </div>

                    <div className="rounded-xl border border-slate-200 overflow-hidden">
                      <table className="w-full text-[11px]">
                        <thead className="bg-slate-50 text-slate-500"><tr><th className="text-left px-2 py-1.5">Data</th><th className="px-2">Valore</th><th className="px-2">Esito</th></tr></thead>
                        <tbody>
                          {[...(serieAnalita.valori || [])].reverse().map((v, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="px-2 py-1.5">{new Date(v.data).toLocaleDateString('it-IT')}</td>
                              <td className={`px-2 text-center font-semibold ${v.fuori_range ? 'text-red-600' : 'text-slate-700'}`}>{v.valore ?? v.valore_testo ?? '-'} {serieAnalita.unita}</td>
                              <td className="px-2 text-center">{v.fuori_range ? <span className="text-red-600">{v.direzione || 'fuori range'}</span> : <span className="text-emerald-600">nei limiti</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      )}

      <ConfirmDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={elimina}
        title="Eliminare il referto?"
        message="Il referto e i suoi valori non saranno piu visibili nella cartella clinica."
      />
    </div>
  );
};

export default LabTab;
